import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { CardNumberElement, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { IconChevronLeft, IconLock } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { GAME_PAYMENT_MODE } from "../../../constants/game";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { formatGameDateTime } from "../../../utils/formatters";
import { formatTeamSize } from "../../../utils/game";
import { DeletePaymentDialog } from "../../payments/MyPayments/parts/PaymentMethods/parts";
import { AddPlayerDialog } from "../GameDetails/parts";
import {
  CancellationPolicyBlock,
  CustomStripeForm,
  PayDirectly,
  Players,
  SavedCards,
} from "./parts";

const ReviewBooking = () => {
  const location = useLocation();
  const gameData = location.state?.gameData || null;
  const unregisteredPlayers = location.state?.unregisteredPlayers || [];
  const isInvite = location.state?.isInvite || false;

  const navigate = useNavigate();
  const { currentUser, fetchUserProfile } = useAuth();
  const { showToast } = useToast();

  const isCurrentUserPlayer = gameData?.active_players?.some(
    (playerId) => playerId === currentUser?.id,
  );

  const [bookingPlayers, setBookingPlayers] = useState([
    ...(isCurrentUserPlayer ? [] : [currentUser]),
    ...unregisteredPlayers,
  ]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);

  const [stripePromise, setStripePromise] = useState(null);
  const [stripeElements, setStripeElements] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const [saveCard, setSaveCard] = useState(true);

  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleOpenDeleteDialog = (method) => {
    setMethodToDelete(method);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMethodToDelete(null);
  };

  const [isPaying, setIsPaying] = useState(false);

  const handleCardReady = useCallback(({ stripe, elements }) => {
    setStripeElements({ stripe, elements });
  }, []);

  const handleFormValidityChange = useCallback((isValid) => {
    setIsFormValid(isValid);
  }, []);

  const unregistered_players = bookingPlayers
    .filter((p) => !p.id)
    .map((player) => ({
      first_name: player.first_name,
      last_name: player.last_name,
    }));

  const handleAcceptInvite = async () => {
    try {
      await apiService.respondToGameInvite(gameData.id, {
        action: "accept",
      });
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  // Calculate totalCost
  const cost = (gameData?.amount_per_player * bookingPlayers.length).toFixed(2);
  const userGameCredits = (currentUser?.credits || 0).toFixed(2);
  const gameCredits = Math.min(userGameCredits, cost);
  const totalCost = (cost - gameCredits).toFixed(2);

  const handlePay = async () => {
    if (!stripeElements) {
      showToast("Payment form not ready", "error");
      return;
    }

    try {
      // Get the card element fresh from elements
      const cardElement = stripeElements.elements.getElement(CardNumberElement);

      if (!cardElement) {
        showToast("Card element not found", "error");
        return;
      }

      setIsPaying(true);
      const response = await apiService.createPaymentIntent({
        game_id: gameData.id,
        save_card: saveCard,
        unregistered_players,
      });
      if (response.success) {
        const { error, paymentIntent } =
          await stripeElements.stripe.confirmCardPayment(
            response.data.client_secret,
            {
              payment_method: { card: cardElement },
            },
          );

        fetchUserProfile(currentUser?.accessToken);
        if (error) {
          showToast("Payment failed: " + error.message, "error");
        } else if (paymentIntent.status === "succeeded") {
          if (isInvite) {
            await handleAcceptInvite();
          }
          showToast("Payment successful!", "success");
          navigate("/game-success", {
            state: {
              gameDetails: gameData,
              playersAdded: unregistered_players.length > 0,
            },
          });
        } else {
          showToast("ℹ️ Status: " + paymentIntent.status, "info");
        }
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsPaying(false);
    }
  };

  const handleJoinGame = async () => {
    try {
      const response = await apiService.joinGame(gameData.id, {
        unregistered_players,
      });
      if (response.success) {
        if (isInvite) {
          await handleAcceptInvite();
        }
        showToast(response.message, "success");

        navigate("/game-success", {
          state: {
            gameDetails: gameData,
            playersAdded: unregistered_players.length > 0,
          },
        });
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  const handleJoinWithSavedCard = async () => {
    setIsPaying(true);
    try {
      const response = await apiService.joinWithSavedCard({
        game_id: gameData.id,
        ...(selectedPaymentMethodId
          ? { payment_method_id: selectedPaymentMethodId }
          : {}),
        unregistered_players,
      });
      if (response.success) {
        if (isInvite) {
          await handleAcceptInvite();
        }
        showToast(response.message, "success");
        fetchUserProfile(currentUser?.accessToken);
        navigate("/game-success", {
          state: {
            gameDetails: gameData,
            playersAdded: unregistered_players.length > 0,
          },
        });
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsPaying(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiService.fetchListPaymentMethods();
      if (response.success) {
        setSavedPaymentMethods(response.data);
        if (totalCost > 0) {
          if (response.data.length > 0) {
            setSelectedPaymentMethodId(response.data[0].payment_method_id);
          } else {
            setSelectedPaymentMethodId("new_card");
          }
        }
      }
    } catch (error) {
      // Handle error if needed
    }
  };

  const handleRemovePaymentMethod = async () => {
    if (!methodToDelete) return;

    setIsDeleting(true);
    try {
      const response = await apiService.deletePaymentMethod(methodToDelete);
      if (response.success) {
        // Refresh payment methods list
        fetchPaymentMethods();
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  useEffect(() => {
    if (!gameData || gameData.payment_mode !== GAME_PAYMENT_MODE.IN_APP) return;
    const fetchStripeKey = async () => {
      try {
        const stripe = await loadStripe(
          process.env.REACT_APP_STRIPE_PUBLIC_KEY,
        );
        setStripePromise(stripe);
      } catch (error) {
        // Handle error if needed
      }
    };
    fetchStripeKey();
    fetchPaymentMethods();
  }, []);

  const handleBack = () => {
    navigate(`/game-details/${gameData.id}`);
  };

  useEffect(() => {
    if (totalCost <= 0) {
      setSelectedPaymentMethodId();
    }
  }, [totalCost]);

  return (
    <Box
      sx={{
        mx: "auto",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Responsive Back Button and Header */}
        <Box sx={{ mb: 3, display: { xs: "block", md: "none" } }}>
          <Button variant="back" onClick={handleBack} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Box
            onClick={handleBack}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              cursor: "pointer",
              color: "#1890FF",
              "&:hover": {
                color: "#40A9FF",
              },
            }}
          >
            <IconChevronLeft size={20} />
          </Box>
          <Text variant="pageHeader">Review your booking</Text>
        </Box>
        <Text component="p" variant="paragraph" sx={{ mb: 3 }}>
          {formatTeamSize(gameData.players)} {gameData.sport} at{" "}
          {gameData?.venue || "venue"} {formatGameDateTime(gameData.datetime)}
        </Text>
        {/* Players Section */}
        <Players
          bookingPlayers={bookingPlayers}
          gameData={gameData}
          openAddPlayerModal={() => {
            setIsAddPlayerModalOpen(true);
          }}
          handleRemovePlayer={(playerId) => {
            setBookingPlayers((prevPlayers) =>
              prevPlayers.filter((player) => player.player_id !== playerId),
            );
          }}
        />
        {/* Payment Details Section */}
        {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP &&
          totalCost > 0 && (
            // Collect payment in app - Direct card details
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                p: 2, // 16px padding
                mb: 3,
                border: "1px solid #E5E5E5",
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Text variant="paragraph" sx={{ fontWeight: 600 }}>
                  Payment details
                </Text>
              </Box>

              <SavedCards
                savedPaymentMethods={savedPaymentMethods}
                selectedPaymentMethodId={selectedPaymentMethodId}
                setSelectedPaymentMethodId={setSelectedPaymentMethodId}
                handleOpenDeleteDialog={handleOpenDeleteDialog}
              />

              {stripePromise && selectedPaymentMethodId === "new_card" && (
                <>
                  <Elements stripe={stripePromise}>
                    <CustomStripeForm
                      onCardReady={handleCardReady}
                      onFormValidityChange={handleFormValidityChange}
                    />
                  </Elements>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mt: 1,
                      mb: -1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveCard}
                          onChange={(e) => setSaveCard(e.target.checked)}
                          sx={{
                            color: "#1890FF",
                            "&.Mui-checked": {
                              color: "#1890FF",
                            },
                          }}
                        />
                      }
                      label={null}
                      sx={{ mr: 0 }}
                    />
                    <Text
                      component="p"
                      variant="paragraph3"
                      sx={{ mt: "10px" }}
                    >
                      Remember my card for next time.
                    </Text>
                  </Box>
                </>
              )}
            </Box>
          )}
        {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP && (
          <CancellationPolicyBlock
            sx={{ display: { xs: "block", md: "none" } }}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
            refundCutoffHours={gameData.refund_cutoff_hours}
          />
        )}
        {gameData?.payment_mode === GAME_PAYMENT_MODE.SELF && (
          // Collect payment myself - Show "Pay the host directly" box
          <PayDirectly />
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          pt: { xs: 0, md: 2 },
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: { md: "0 2px 8px rgba(0,0,0,0.1)" },
          width: { xs: "auto", md: "250px", lg: "256px" },
          position: { md: "sticky" },
          top: { xs: "auto", md: 0 },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
          height: "fit-content",
        }}
      >
        {/* Terms and Conditions - Only for app payments */}
        {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP && (
          <CancellationPolicyBlock
            sx={{ display: { xs: "none", md: "block" } }}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
          />
        )}
        <Button
          variant="primary"
          onClick={() => {
            if (
              gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP &&
              selectedPaymentMethodId === "new_card" &&
              totalCost > 0
            ) {
              handlePay();
            } else if (
              (gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP &&
                selectedPaymentMethodId !== "new_card" &&
                selectedPaymentMethodId) ||
              (gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP &&
                totalCost <= 0)
            ) {
              handleJoinWithSavedCard();
            } else {
              handleJoinGame();
            }
          }}
          disabled={
            gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP
              ? (!selectedPaymentMethodId && totalCost > 0) ||
                (selectedPaymentMethodId === "new_card" &&
                  !isFormValid &&
                  totalCost > 0) ||
                !agreedToTerms ||
                isPaying
              : false
          }
          sx={{
            width: "100%",
            py: 1.5,
            fontSize: "16px",
          }}
        >
          {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP
            ? isPaying
              ? "Processing payment..."
              : "Confirm booking and pay"
            : "Confirm booking"}
        </Button>
        {gameData?.payment_mode === GAME_PAYMENT_MODE.IN_APP && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#A1A1A1",
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <IconLock size={20} style={{ flexShrink: 0 }} />
            <Text sx={{ color: "#A1A1A1" }} variant="paragraph3">
              Secure payment powered by Stripe
            </Text>
          </Box>
        )}
      </Box>

      {/* Add Player Modal */}
      <AddPlayerDialog
        isOpen={isAddPlayerModalOpen}
        onClose={() => setIsAddPlayerModalOpen(false)}
        onSubmit={(newPlayerData) => {
          setIsAddPlayerModalOpen(false);
          setBookingPlayers((prevPlayers) => [
            ...prevPlayers,
            { ...newPlayerData, player_id: crypto.randomUUID() },
          ]);
        }}
        players={bookingPlayers}
      />

      {/* Delete Confirmation Dialog */}
      <DeletePaymentDialog
        isOpen={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleRemovePaymentMethod}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default ReviewBooking;
